import { collectionOptions } from "@tanstack/db";
import { hashKey, type QueryClient } from "@tanstack/query-core";
import {
  createCursorPager,
  queryCollectionOptions,
} from "@tanstack/query-db-collection";
import { apiDelete, apiGet } from "@softmax/observatory-client/client";
import type {
  PostCommentPublic,
  PostDetailPublic,
  PostFeedPage,
  PostPublic,
} from "@softmax/observatory-client/types";

export type PostSort = "hot" | "new" | "top";
export type PostFeedScope = {
  forumSlug: string;
  limit: number;
  sort: PostSort;
  elevatedPrivileges: boolean;
};
type FeedPost = PostPublic & { feedPosition: number };

export function postFeedCollection(scope: PostFeedScope) {
  const { forumSlug, limit, sort, elevatedPrivileges } = scope;
  const queryKey = ["posts", "feed", scope] as const;
  return collectionOptions(hashKey(queryKey), (db) => {
    const queryClient = db.requireDependency<QueryClient>("queryClient");
    const pager = createCursorPager<PostPublic>({
      queryClient,
      queryKey: [...queryKey, "pages"],
      staleTime: 30_000,
      fetchPage: async (cursor, signal) => {
        const params = new URLSearchParams({ limit: String(limit), sort });

        if (cursor) params.set("cursor", cursor);

        const page = await apiGet<PostFeedPage>(
          `/v2/forums/${encodeURIComponent(forumSlug)}/posts?${params}`,
          { signal, elevatedPrivileges },
        );
        return { rows: page.entries, nextCursor: page.next_cursor };
      },
    });
    return queryCollectionOptions<FeedPost>({
      queryClient,
      queryKey: [...queryKey, "rows"],
      syncMode: "on-demand",
      queryFn: async ({ meta, signal }) => {
        const { offset = 0, limit: windowLimit } =
          meta?.loadSubsetOptions ?? {};
        const window: Parameters<typeof pager.read>[0] = { offset };

        if (windowLimit !== undefined) window.limit = windowLimit;

        const posts = await pager.read(window, signal);
        return posts.map((post, index) => ({
          ...post,
          feedPosition: offset + index,
        }));
      },
      getKey: (post) => post.id,
      staleTime: 30_000,
    });
  });
}

export function postDetailCollections({
  postId,
  elevatedPrivileges,
  initialDetail,
}: {
  postId: string;
  elevatedPrivileges: boolean;
  initialDetail?: PostDetailPublic | null;
}) {
  const queryKey = ["posts", "detail", postId, { elevatedPrivileges }] as const;
  const queryFn = async ({ signal }: { signal: AbortSignal }) => {
    const detail = await apiGet<PostDetailPublic>(
      `/v2/posts/${encodeURIComponent(postId)}`,
      { signal, elevatedPrivileges },
    );
    return { posts: [detail.post], comments: detail.comments };
  };
  const initialData = initialDetail
    ? { posts: [initialDetail.post], comments: initialDetail.comments }
    : undefined;
  return {
    post: collectionOptions(`${hashKey(queryKey)}:post`, (db) =>
      queryCollectionOptions<PostPublic, typeof queryFn>({
        queryClient: db.requireDependency<QueryClient>("queryClient"),
        queryKey,
        queryFn,
        initialData,
        select: (detail) => detail.posts,
        getKey: (post) => post.id,
        staleTime: 30_000,
      }),
    ),
    comments: collectionOptions(`${hashKey(queryKey)}:comments`, (db) =>
      queryCollectionOptions<PostCommentPublic, typeof queryFn>({
        queryClient: db.requireDependency<QueryClient>("queryClient"),
        queryKey,
        queryFn,
        initialData,
        select: (detail) => detail.comments,
        getKey: (comment) => comment.id,
        staleTime: 30_000,
        onDelete: async ({ transaction, collection }) => {
          const signal =
            db.requireDependency<AbortController>("lifetime").signal;

          for (const mutation of transaction.mutations) {
            await apiDelete<void>(
              `/v2/posts/${encodeURIComponent(postId)}/comments/${encodeURIComponent(mutation.key)}`,
              { reason: "Author removed their comment" },
              { elevatedPrivileges, signal },
            ).catch((error: Error) => {
              db.requireDependency<(error: Error) => void>(
                "reportAuthorizationError",
              )(error);
              throw error;
            });
            signal.throwIfAborted();
            await collection.utils.writeUpsert({
              ...mutation.original,
              body: "[deleted]",
              deleted_at: new Date().toISOString(),
            });
          }

          signal.throwIfAborted();
          void collection.utils.refetch();
        },
      }),
    ),
  };
}
